// <s-cart-recommendations> loads Search & Discovery product recommendations for the cart drawer.
// It reads the JSON recommendations endpoint (data-url, written from Liquid routes) because the
// section-less HTML recommendations route is rejected by the storefront. Products already in the
// cart (data-exclude) are skipped, results are capped at data-limit, and the element hides itself
// when nothing comes back. The cart-drawer partial re-renders this element after every cart change,
// so each connection re-seeds from the current cart.
class CartRecommendations extends HTMLElement {
  #observer = null;
  #controller = null;

  connectedCallback() {
    if (!this.dataset.url || !this.dataset.productId) {
      this.hidden = true;
      return;
    }

    // The drawer is closed on load; wait until the list is actually visible before fetching.
    this.#observer = new IntersectionObserver((entries, observer) => {
      if (!entries.some((entry) => entry.isIntersecting)) return;
      observer.disconnect();
      this.#load();
    });
    this.#observer.observe(this);
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
    this.#controller?.abort();
  }

  async #load() {
    this.#controller = new AbortController();
    const limit = parseInt(this.dataset.limit, 10) || 3;
    const exclude = new Set((this.dataset.exclude || '').split(',').filter(Boolean));
    const params = new URLSearchParams({
      product_id: this.dataset.productId,
      intent: this.dataset.intent || 'related',
      limit: String(limit + exclude.size + 2),
    });

    this.setAttribute('aria-busy', 'true');
    try {
      const response = await fetch(`${this.dataset.url}?${params}`, {
        signal: this.#controller.signal,
        headers: { Accept: 'application/json' },
      });
      if (!response.ok) throw new Error(`Recommendations request failed: ${response.status}`);
      const data = await response.json();

      const seen = new Set();
      const products = (data.products || []).filter((product) => {
        const id = String(product.id);
        if (exclude.has(id) || seen.has(id) || product.available === false) return false;
        seen.add(id);
        return true;
      }).slice(0, limit);

      this.#render(products);
    } catch (error) {
      if (error.name !== 'AbortError') this.hidden = true;
    } finally {
      this.removeAttribute('aria-busy');
      this.#controller = null;
    }
  }

  #render(products) {
    const list = this.querySelector('[data-rec-list]');
    const template = this.querySelector('template[data-rec-template]');
    if (!list || !template || products.length === 0) {
      this.hidden = true;
      return;
    }

    const formatter = this.#priceFormatter();
    const items = products.map((product) => {
      const fragment = template.content.cloneNode(true);
      const link = fragment.querySelector('[data-rec-link]');
      const image = fragment.querySelector('[data-rec-image]');
      const title = fragment.querySelector('[data-rec-title]');
      const price = fragment.querySelector('[data-rec-price]');

      if (link) {
        link.href = product.url || '';
        link.setAttribute('aria-label', product.title);
      }
      if (title) title.textContent = product.title;
      if (price) price.textContent = formatter(product.price);
      if (image) {
        if (product.featured_image) {
          const src = new URL(product.featured_image, window.location.origin);
          src.searchParams.set('width', '240');
          image.src = src.toString();
          image.alt = product.title;
        } else {
          image.remove();
        }
      }
      return fragment;
    });

    list.replaceChildren(...items);
    this.hidden = false;
  }

  #priceFormatter() {
    const currency = this.dataset.currency;
    let format;
    try {
      format = new Intl.NumberFormat(this.dataset.locale || undefined, { style: 'currency', currency });
    } catch {
      try {
        format = new Intl.NumberFormat(undefined, { style: 'currency', currency });
      } catch {
        format = null;
      }
    }
    // Recommendation JSON prices are integer minor units of the buyer's presentment currency.
    return (cents) => (format ? format.format(cents / 100) : (cents / 100).toFixed(2));
  }
}

if (!customElements.get('s-cart-recommendations')) {
  customElements.define('s-cart-recommendations', CartRecommendations);
}
